import type { ExerciseQueue } from 'api/rest/exerciseQueues/exerciseQueues.types';
import type { Section } from 'api/rest/sections/sections.types';

import { SORT_OPTIONS } from '../Initialize/onGoInitialize.constants';
import type { OnGoInitializeFormValues } from '../Initialize/onGoInitialize.types';

const MINUTES_PER_EXERCISE = 3;

/** Polish minute label for a count (ok. 1 minuta, 2 minuty, 5 minut). */
export const formatEstimatedMinutes = (minutes: number): string => {
  if (minutes === 1) {
    return 'ok. 1 minuta';
  }
  const lastDigit = minutes % 10;
  const lastTwoDigits = minutes % 100;
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return `ok. ${minutes} minuty`;
  }
  return `ok. ${minutes} minut`;
};

/** Polish noun for queue length (zadanie, zadania, zadań). */
export const formatQueueLengthNoun = (count: number): string => {
  if (count === 1) {
    return 'zadanie';
  }
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return 'zadania';
  }
  return 'zadań';
};

/** Polish label for queue length (1 zadanie, 2 zadania, 5 zadań). */
export const formatQueueLengthLabel = (count: number): string => {
  if (count === 1) {
    return '1 zadanie';
  }
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return `${count} zadania`;
  }
  return `${count} zadań`;
};

export type QueuePreviewScopeEntry = {
  sectionName: string;
  allSubsectionsSelected: boolean;
  subsectionNames: string[];
  subsectionCount: number;
};

export type QueuePreviewDetails = {
  queueLength: number;
  sortLabel: string;
  allSectionsSelected: boolean;
  scopeEntries: QueuePreviewScopeEntry[];
  exerciseTypeLabels: string[];
  estimatedDurationLabel: string;
};

const resolveScopeEntries = (formValues: OnGoInitializeFormValues, sectionsData: Section[]): QueuePreviewScopeEntry[] => {
  const sectionById = new Map(sectionsData.map(section => [String(section.id), section]));

  return Object.entries(formValues.scope.sections).flatMap(([sectionId, sectionSelection]): QueuePreviewScopeEntry[] => {
    const section = sectionById.get(sectionId);
    if (!section) {
      return [];
    }

    if (sectionSelection.allSelected === 'checked') {
      const subsectionNames = section.subsections.map(subsection => subsection.name);

      return [
        {
          sectionName: section.name,
          allSubsectionsSelected: true,
          subsectionNames,
          subsectionCount: subsectionNames.length,
        },
      ];
    }

    if (sectionSelection.allSelected === 'indeterminate') {
      const subsectionNames = section.subsections
        .filter(subsection => sectionSelection.subsections[String(subsection.id)])
        .map(subsection => subsection.name);

      if (subsectionNames.length === 0) {
        return [];
      }

      return [
        {
          sectionName: section.name,
          allSubsectionsSelected: false,
          subsectionNames,
          subsectionCount: subsectionNames.length,
        },
      ];
    }

    return [];
  });
};

const resolveSortLabel = (formValues: OnGoInitializeFormValues): string => {
  return SORT_OPTIONS.find(option => option.value === formValues.sort)?.label ?? formValues.sort;
};

const resolveExerciseTypeLabels = (formValues: OnGoInitializeFormValues): string[] => {
  if (formValues.unansweredOnly) {
    return ['Nierozwiązane zadania'];
  }

  const labels: string[] = ['Nierozwiązane zadania'];
  if (formValues.includeCorrectlyAnswered) {
    labels.push('Poprawnie rozwiązane');
  }
  if (formValues.includeIncorrectlyAnswered) {
    labels.push('Błędnie rozwiązane');
  }
  return labels;
};

export const resolveQueuePreviewDetails = (
  formValues: OnGoInitializeFormValues,
  sectionsData: Section[],
  queue: ExerciseQueue,
): QueuePreviewDetails => {
  const queueLength = queue.progress.total;
  const estimatedMinutes = queueLength * MINUTES_PER_EXERCISE;

  return {
    queueLength,
    sortLabel: resolveSortLabel(formValues),
    allSectionsSelected: formValues.scope.allSelected === 'checked',
    scopeEntries: resolveScopeEntries(formValues, sectionsData),
    exerciseTypeLabels: resolveExerciseTypeLabels(formValues),
    estimatedDurationLabel: formatEstimatedMinutes(estimatedMinutes),
  };
};
